import React, { useState, useEffect } from 'react';
import StaticDataApi from '../../api/platform/staticDataApi';
import AccountApi from '../../api/platform/accountApi';
import UserApi from '../../api/platform/userApi';
import { useSystemError } from '../../components/Error/SystemErrorContext';
import styles from '../../css/setting/settings-content.module.css';
import { AccountModel } from '../../models/platform/accountModel';
import { CompanySizesModel } from '../../models/platform/companySizesModel';
import { ResponseErrorModel } from '../../models/system/responseErrorModel';
import { ResponseSuccessModel } from '../../models/system/responseSuccessModel';
import {
  handleError,
  mapResponseErrorToSystemError,
  mapUnknownErrorToSystemError
} from '../../utils/errorUtils';

const AccountSetting: React.FC = () => {
  const [companyName, setCompanyName] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [companySizes, setCompanySizes] = useState<CompanySizesModel[]>([]);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [didErrorOccur, setDidErrorOccur] = useState(false);
  const { triggerSystemError } = useSystemError();

  useEffect(() => {
    const fetchData = async () => {
      try {
        await fetchCompanySizes();
        await fetchAccount();
      } catch (error) {
        handleError(
          mapUnknownErrorToSystemError(error, false),
          setErrorMessage,
          setDidErrorOccur,
          triggerSystemError
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [triggerSystemError]);

  const fetchCompanySizes = async () => {
    const response = await StaticDataApi.getCompanySizes();
    if (response.success) {
      const data = (response as ResponseSuccessModel).data;
      setCompanySizes(data);
    } else {
      handleError(
        mapResponseErrorToSystemError(response as ResponseErrorModel),
        setErrorMessage,
        setDidErrorOccur,
        triggerSystemError
      );
    }
  };

  const fetchAccount = async () => {
    const response = await UserApi.getUser();
    if (response.success) {
      const data = (response as ResponseSuccessModel).data.account as AccountModel;
      setCompanyName(data.company_name || '');
      setCompanySize(data.company_size || '');
    } else {
      handleError(
        mapResponseErrorToSystemError(response as ResponseErrorModel),
        setErrorMessage,
        setDidErrorOccur,
        triggerSystemError
      );
    }
  };

  const handleSave = async () => {
    if (!companyName.trim() || !companySize.trim()) {
      setErrorMessage('Please provide both company name and size.');
      return;
    }

    try {
      const response = await AccountApi.updateAccount(companyName, companySize);
      if (response.success) {
        setSuccessMessage('Company info updated successfully!');
        setErrorMessage(null);
      } else {
        handleError(
          mapResponseErrorToSystemError(response as ResponseErrorModel),
          setErrorMessage,
          setDidErrorOccur,
          triggerSystemError
        );
      }
    } catch (error) {
      handleError(
        mapUnknownErrorToSystemError(error, false),
        setErrorMessage,
        setDidErrorOccur,
        triggerSystemError
      );
    }
  };

  return (
    <div className={styles.settingsContainer}>
      {loading ? (
        <div className={styles.spinner}>Loading...</div>
      ) : (
        <form
          className={styles.formLayout}
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          <div className={styles.formGroup}>
            <label htmlFor="companyName">Company Name</label>
            <input
              type="text"
              id="companyName"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="companySize">Company Size</label>
            <select
              id="companySize"
              value={companySize}
              onChange={(e) => setCompanySize(e.target.value)}
              className={styles.selectInput}
            >
              <option value="">Select company size</option>
              {companySizes.map((size) => (
                <option key={size.value} value={size.value}>
                  {size.label}
                </option>
              ))}
            </select>
          </div>

          <button type="submit" className={styles.submitButton}>
            Save
          </button>

          {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}
          {successMessage && <div className={styles.successMessage}>{successMessage}</div>}
        </form>
      )}
    </div>
  );
};

export default AccountSetting;
